import "./VendorApproval.css";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuthStore } from "../../Services/authStore";
import { toastSuccess, toastError, toastWarning } from "../../Services/toaster";

// ── Format Currency ──
function formatCurrency(amount) {
  if (!amount && amount !== 0) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

// ── Remark Modal ──────────────────────────────────────────
function VendorRemarkModal({ action, onConfirm, onCancel }) {
  const [remark, setRemark] = useState("");
  const isApprove = action === "Approved";

  const handleSubmit = () => {
    if (!isApprove && !remark.trim()) {
      toastWarning("Remarks are mandatory for rejection.");
      return;
    }
    onConfirm(remark.trim());
  };

  return (
    <div className="vap-modal-overlay">
      <div className="vap-modal">
        <div className="vap-modal-icon">{isApprove ? "✅" : "❌"}</div>
        <h3 className="vap-modal-title">
          {isApprove ? "Approve Vendor" : "Reject Vendor"}
        </h3>
        <p className="vap-modal-sub">
          {isApprove
            ? "Add optional remarks before approving."
            : "Remarks are mandatory before rejecting."}
        </p>
        <textarea
          className="vap-modal-textarea"
          rows={3}
          placeholder={
            isApprove
              ? "Optional remarks…"
              : "Enter reason for rejection (required)…"
          }
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
          autoFocus
        />
        <div className="vap-modal-actions">
          <button
            className="vap-modal-btn vap-modal-btn-ghost"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className={`vap-modal-btn ${isApprove ? "vap-modal-btn-approve" : "vap-modal-btn-reject"}`}
            onClick={handleSubmit}
          >
            {isApprove ? "Approve" : "Reject"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Vendor Approval Card ─────────────────────────────────────────
function VendorApprovalCard({ row, onApprove, onReject }) {
  const approvalStatus = row.approval_status || "Pending";
  const canAct = row.can_take_action === true;
  const isPending = approvalStatus === "Pending";
  const isApproved = approvalStatus === "Approved";
  const isRejected = approvalStatus === "Rejected";

  const statusColor = {
    Approved: { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
    Rejected: { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" },
    Pending: { bg: "#fffbeb", color: "#b45309", border: "#fde68a" },
  };
  const sc = statusColor[approvalStatus] || statusColor.Pending;

  const barColor = isApproved ? "#15803d" : isRejected ? "#b91c1c" : "#A27B5B";

  return (
    <div className="vap-card">
      <div className="vap-card-bar" style={{ background: barColor }} />

      <div className="vap-card-icon">
        <div className="module-icon">📄</div>
      </div>

      <div className="vap-card-main">
        <div className="vap-card-title">{row.vendor_name}</div>
        <div className="vap-card-tag">VENDOR APPROVAL</div>
        <div className="vap-card-desc">
          {row.category} — {row.cost_model} · {formatCurrency(row.rate)}
        </div>
      </div>

      <div className="vap-card-divider" />

      <div className="vap-card-meta">
        <div className="vap-meta-row">
          <span className="vap-meta-key">Vendor Code:</span>
          <span className="vap-meta-val">{row.vendor_code}</span>
        </div>
        <div className="vap-meta-row">
          <span className="vap-meta-key">Contact:</span>
          <span className="vap-meta-val">{row.contact_person}</span>
        </div>
        <div className="vap-meta-row">
          <span className="vap-meta-key">Email:</span>
          <span className="vap-meta-val">{row.email}</span>
        </div>
        <div className="vap-meta-row">
          <span className="vap-meta-key">Mobile:</span>
          <span className="vap-meta-val">{row.mobile_no}</span>
        </div>
        <div className="vap-meta-row vap-meta-status">
          <span
            className="vap-status-pill"
            style={{
              background: sc.bg,
              color: sc.color,
              border: `1px solid ${sc.border}`,
            }}
          >
            {approvalStatus}
          </span>
        </div>
        {row.remarks && (
          <div className="vap-meta-row vap-meta-remarks">
            <span className="vap-meta-key">Remarks:</span>
            <span className="vap-meta-val vap-meta-remarks-text">
              "{row.remarks}"
            </span>
          </div>
        )}
      </div>

      <div className="vap-card-divider" />

      <div className="vap-card-actions">
        {isPending && canAct ? (
          <>
            <button
              className="vap-btn vap-btn-approve"
              onClick={() => onApprove(row)}
            >
              Approve
            </button>
            <button
              className="vap-btn vap-btn-reject"
              onClick={() => onReject(row)}
            >
              Reject
            </button>
          </>
        ) : isPending && !canAct ? (
          <div className="vap-actioned vap-actioned-waiting">
            ⏳ Awaiting your turn
          </div>
        ) : (
          <>
            <div
              className={`vap-actioned ${isApproved ? "vap-actioned-ok" : "vap-actioned-no"}`}
            >
              {isApproved ? "✓ Approved" : "✕ Rejected"}
            </div>
            {row.remarks && <div className="vap-remarks">"{row.remarks}"</div>}
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
//  VENDOR APPROVAL VIEW
// ─────────────────────────────────────────────────────────
export default function VendorApprovalView() {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter] = useState("All");
  const [remarkModal, setRemarkModal] = useState(null);

  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const API = process.env.REACT_APP_API_URL;

  const hdrs = useCallback(
    () => ({
      Authorization: `Bearer ${token}`,
      accept: "application/json",
    }),
    [token],
  );

  const fetchApprovals = useCallback(async () => {
    setLoading(true);
    try {
      const userId = user?.user_id;

      const res = await axios.get(`${API}/vendor/my-approvals-vendor`, {
        headers: hdrs(),
        params: {
          user_id: userId,
        },
      });

      const data = res.data?.data;
      const mappedData = Array.isArray(data)
        ? data.map((item) => ({
            id: item.id,
            vendor_id: item.vendor_id,
            vendor_code: item.vendor_code,
            vendor_name: item.vendor_name,
            contact_person: item.contact_person,
            mobile_no: item.mobile_no,
            email: item.email,
            category: item.category,
            cost_model: item.cost_model,
            rate: item.rate,
            address: item.address,
            notes: item.notes,
            approval_status: item.approval_status,
            can_take_action: item.can_take_action,
            remarks: item.remarks || "",
            approval_level: item.approval_level,
          }))
        : [];

      setApprovals(mappedData);
    } catch (err) {
      console.error("Failed to load vendor approvals:", err.response?.data);
      toastError("Failed to load vendor approvals.");
    } finally {
      setLoading(false);
    }
  }, [API, hdrs, user]);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  const handleApprove = (row) => {
    setRemarkModal({ row, action: "Approved" });
  };

  const handleReject = (row) => {
    setRemarkModal({ row, action: "Rejected" });
  };

  const handleConfirm = async (remarks) => {
    const { row, action } = remarkModal;
    setRemarkModal(null);

    try {
      const authToken = useAuthStore.getState().token;
      const userId = useAuthStore.getState().user?.id;

      // Match the exact payload structure from Swagger
      const payload = {
        id: row.id,
        vendor_id: row.vendor_id,
        user_id: userId,
        status: action,
        remarks: remarks || "",
      };

      await axios.post(`${API}/vendor/my-approvals-vendor-action`, payload, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      toastSuccess(
        action === "Approved"
          ? "Vendor approved successfully."
          : "Vendor rejected successfully.",
      );
      fetchApprovals();
    } catch (err) {
      console.error("Approval action failed:", err.response?.data);
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.join(".")} — ${d.msg}`).join("\n")
        : detail || "Action failed. Please try again.";
      toastError(msg);
    }
  };

  const pending = approvals.filter((r) => r.approval_status === "Pending");
  const approved = approvals.filter((r) => r.approval_status === "Approved");
  const rejected = approvals.filter((r) => r.approval_status === "Rejected");

  const filtered =
    filter === "All"
      ? approvals
      : filter === "Pending"
        ? pending
        : filter === "Approved"
          ? approved
          : rejected;

  return (
    <div className="vap-page">
      {remarkModal && (
        <VendorRemarkModal
          action={remarkModal.action}
          onConfirm={handleConfirm}
          onCancel={() => setRemarkModal(null)}
        />
      )}

      <div className="vap-header">
        <div>
          <h1 className="vap-title">Vendor Approvals</h1>
          <p className="vap-sub">
            Review and action pending vendor approval requests
          </p>
        </div>

        <div className="vap-summary">
          <div className="vap-summary-pill vap-sum-pending">
            <span className="vap-sum-num">{pending.length}</span>
            <span className="vap-sum-lbl">Pending</span>
          </div>
          <div className="vap-summary-pill vap-sum-approved">
            <span className="vap-sum-num">{approved.length}</span>
            <span className="vap-sum-lbl">Approved</span>
          </div>
          <div className="vap-summary-pill vap-sum-rejected">
            <span className="vap-sum-num">{rejected.length}</span>
            <span className="vap-sum-lbl">Rejected</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="vap-empty">Loading vendor approvals…</div>
      ) : filtered.length === 0 ? (
        <div className="vap-empty">
          <div className="vap-empty-icon">✅</div>
          <p>No vendor approval requests found.</p>
        </div>
      ) : (
        <div className="vap-list">
          {filtered.map((row, idx) => (
            <VendorApprovalCard
              key={`${row.id}-${idx}`}
              row={row}
              onApprove={handleApprove}
              onReject={handleReject}
            />
          ))}
        </div>
      )}
    </div>
  );
}
